import type { TranslationKey } from '../i18n'
import type { EntityBatchSyncStatus } from './entityMutationSync'

// Surfaces that render a sync-status panel and need a unified outcome
// derivation. Each surface owns its own i18n namespace, but the decision
// logic for which key to pick is identical across all of them.
//
// Closes part of v2.1 finding #24: previously there were eight near-identical
// `derive*SyncOutcome` helpers (biomass / lots / water / feed / inventory /
// task / editFarm / datatable) that diverged only in the namespace prefix
// — and two of those prefixes did NOT match their helper file name
// (`inventory` writes `warehouse.sync.*`, `task` writes `tasks.sync.*`). The
// wrappers in `<surface>SyncOutcome.ts` stay in place as thin re-exports so
// call sites and existing tests do not have to change.
//
// `productionUnit` is intentionally excluded because its helper returns a
// hardcoded English `resultLabel: string` rather than an i18n key — kept
// as-is to avoid a behavior change in this consolidation pass.
export type SyncOutcomeSurfaceKey =
  | 'biomass'
  | 'lots'
  | 'water'
  | 'feed'
  | 'inventory'
  | 'task'
  | 'editFarm'
  | 'datatable'

export type SyncOutcome = {
  resultKey: TranslationKey
  resultParams?: Record<string, string | number>
}

// Maps the logical surface key to the actual i18n namespace prefix used by
// that surface today. NOTE: `inventory` uses `warehouse.sync.*` and `task`
// uses `tasks.sync.*` (plural) — preserved verbatim from the original
// per-surface helpers so existing translations keep resolving.
const SURFACE_PREFIX: Record<SyncOutcomeSurfaceKey, string> = {
  biomass: 'biomass.sync',
  lots: 'lots.sync',
  water: 'water.sync',
  feed: 'feed.sync',
  inventory: 'warehouse.sync',
  task: 'tasks.sync',
  editFarm: 'editFarm.sync',
  datatable: 'datatable.sync'
}

// Maps the shared logical state to the per-surface translation key suffix.
// Kept verbatim from the original nine helpers — the only divergence was
// the namespace prefix, which now lives in SURFACE_PREFIX above.
const SUFFIX = {
  retrySynced: 'retrySynced',
  retryOffline: 'retryOffline',
  retryPending: 'retryPending',
  retryResolvedMissing: 'retryResolvedMissing',
  blocked: 'blocked',
  blockedWithReason: 'blockedWithReason'
} as const

const composeKey = (surfaceKey: SyncOutcomeSurfaceKey, suffix: string): TranslationKey =>
  `${SURFACE_PREFIX[surfaceKey]}.${suffix}` as TranslationKey

export const deriveSyncOutcome = (params: {
  surfaceKey: SyncOutcomeSurfaceKey
  status: EntityBatchSyncStatus
  skippedNotFoundCount: number
}): SyncOutcome => {
  const { surfaceKey, status, skippedNotFoundCount } = params

  if (skippedNotFoundCount > 0) {
    return {
      resultKey: composeKey(surfaceKey, SUFFIX.retryResolvedMissing),
      resultParams: { count: skippedNotFoundCount }
    }
  }

  if (status.blockedCount > 0) {
    if (status.errorMessage) {
      return {
        resultKey: composeKey(surfaceKey, SUFFIX.blockedWithReason),
        resultParams: {
          count: status.blockedCount,
          reason: status.errorMessage
        }
      }
    }
    return {
      resultKey: composeKey(surfaceKey, SUFFIX.blocked),
      resultParams: { count: status.blockedCount }
    }
  }

  if (status.syncState === 'synced') {
    return { resultKey: composeKey(surfaceKey, SUFFIX.retrySynced) }
  }

  if (status.syncState === 'offline') {
    return { resultKey: composeKey(surfaceKey, SUFFIX.retryOffline) }
  }

  return { resultKey: composeKey(surfaceKey, SUFFIX.retryPending) }
}
